import { useDeferredValue, useEffect, useMemo, useState } from 'react'

import { IssueGalleryModal } from './IssueGalleryModal'
import { IssuesDashboardSkeleton } from './IssuesDashboardSkeleton'
import { IssuesListBody } from './IssuesListBody'
import { useGisIssues } from '../../../hooks/useGisIssues'
import { useResolutionTargets } from '../../../hooks/useResolutionTargets'
import { filterByFacets, issueRiskSort, type IssueFacets } from '../../../lib/issue-board'
import { groupIssues, groupLabelFor } from '../../../lib/issue-groups'
import { useGisIssueView } from '../../../stores/gisIssueViewStore'
import { PanelCard } from '../../ui/PanelCard'

import type { GisIssuesPage, GisScope, GisSite } from '../../../api/types'

const EMPTY_PAGE: GisIssuesPage = { issues: [], totalCount: 0, hasMore: false }

interface IssuesDashboardProps {
  scope: GisScope
  sites: GisSite[]
  activeMandals: readonly string[]
  /** Open an issue in the detail surface (map popup in Split, right drawer in Full). */
  onSelectIssue: (id: string, siteId: string | null) => void
  /** Currently-open issue id (drives the row highlight); null when no detail is open. */
  selectedIssueId: string | null
}

/**
 * The Issues list (V5 toolbar layout): the risk-sorted, faceted, searchable, groupable list fills
 * the panel. All controls live in the page-level `IssuesToolbar`; this component only READS the
 * shared view store and renders the working set. Gallery clicks open the local centered modal —
 * they never touch the drill stack, so the modal and the detail surface are mutually exclusive.
 */
export function IssuesDashboard({ scope, sites, activeMandals, onSelectIssue, selectedIssueId }: IssuesDashboardProps) {
  const { data, isPending, isError, refetch } = useGisIssues(scope)
  const v = useGisIssueView
  const statusView = v((s) => s.statusView)
  const severity = v((s) => s.severity)
  const category = v((s) => s.category)
  const issueType = v((s) => s.issueType)
  const entity = v((s) => s.entity)
  const department = v((s) => s.department)
  const hod = v((s) => s.hod)
  const query = v((s) => s.query)
  const ageBands = v((s) => s.ageBands)
  const siteNames = v((s) => s.siteNames)
  const groupBy = v((s) => s.groupBy)
  const listView = v((s) => s.listView)
  const setHoveredSite = v((s) => s.setHoveredSite)
  // Typing stays responsive; the list reconciles at the deferred value.
  const deferredQuery = useDeferredValue(query)
  const [galleryIssueId, setGalleryIssueId] = useState<string | null>(null)
  const targets = useResolutionTargets()

  // Leaving/entering Gallery drops the modal so it can never linger behind a view switch.
  useEffect(() => {
    setGalleryIssueId(null)
  }, [listView])

  const page = data ?? EMPTY_PAGE
  const feed = page.issues
  const siteMandal = useMemo(() => new Map(sites.map((s) => [s.id, s.mandal])), [sites])
  const facets: IssueFacets = {
    statusView, severity, category, issueType, entity, department, hod,
    query: deferredQuery, mandal: activeMandals, age: ageBands, site: siteNames,
  }
  const rows = useMemo(
    () => issueRiskSort(filterByFacets(feed, facets, siteMandal), targets),

    [feed, statusView, severity, category, issueType, entity, department, hod, deferredQuery, activeMandals, ageBands, siteNames, siteMandal, targets],
  )
  const groups = useMemo(
    () => groupIssues(rows, groupBy, siteMandal),
    [rows, groupBy, siteMandal],
  )

  // Board click router: Gallery → local modal, List → the page's detail surface.
  const openFromBoard = (id: string, siteId: string | null): void => {
    if (listView === 'gallery') {
      setGalleryIssueId(id)
      return
    }
    onSelectIssue(id, siteId)
  }

  if (isPending && data === undefined) {
    return <IssuesDashboardSkeleton />
  }

  const grouped = groupBy !== 'none'
  const subtitle =
    `Showing ${rows.length} issue${rows.length === 1 ? '' : 's'} · Sorted by risk${ 
    grouped ? ` · Grouped by ${groupLabelFor(groupBy).toLowerCase()}` : ''}`

  return (
    <div className="grid min-h-0 flex-1 grid-rows-[minmax(0,1fr)] p-3">
      <PanelCard title="Issues List" subtitle={subtitle} fillBody className="min-h-0">
        <IssuesListBody
          view={listView}
          groupBy={groupBy}
          groups={groups}
          isPending={isPending}
          isError={isError}
          onRetry={() => void refetch()}
          rows={rows}
          hasAnyIssues={feed.length > 0}
          targets={targets}
          siteMandal={siteMandal}
          selectedId={selectedIssueId}
          onSelect={openFromBoard}
          onHover={setHoveredSite}
        />
      </PanelCard>
      <IssueGalleryModal
        open={listView === 'gallery'}
        issueId={galleryIssueId}
        rows={rows}
        onNavigate={setGalleryIssueId}
        onClose={() => setGalleryIssueId(null)}
      />
    </div>
  )
}
